// useFilters.js - Custom hook to handle filters and query params
import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const toComparable = (sp, ignoreKeys = ["offset"]) => {
  const entries = [];
  sp.forEach((value, key) => {
    if (!ignoreKeys.includes(key)) {
      entries.push([key, value]);
    }
  });
  entries.sort((a, b) => (a[0] === b[0] ? a[1].localeCompare(b[1]) : a[0].localeCompare(b[0])));
  return entries.map(([k, v]) => `${k}=${v}`).join("&");
};

const buildNextParams = ({ base, extraParams, showApplyFiltersButton }) => {
  const next = new URLSearchParams(base);

  if (extraParams && typeof extraParams === "object") {
    Object.entries(extraParams).forEach(([k, v]) => {
      if (v === null || v === "") {
        next.delete(k);
      } else if (Array.isArray(v)) {
        next.set(k, v.join(","));
      } else {
        next.set(k, v);
      }
    });
  }

  if (showApplyFiltersButton) {
    next.set("offset", "0");
  }

  return next;
};

export function useQueryParams(options = {}) {
  const { showApplyFiltersButton = false, debounceMs = 300, replaceNavigation = false } = options;

  const location = useLocation();
  const navigate = useNavigate();
  const [filters, setFilters] = useState(new URLSearchParams(location.search));
  const debounceTimeoutRef = useRef(null);

  const LOOKUPS = { __IN: "__in" };

  const customFiltersList = {
    doc_type: {
      lookup_key: LOOKUPS.__IN,
      can_modify: true,
    },
    doc_type_not__in: {
      lookup_key: "",
      can_modify: false,
    },
    incident_location__not_in: {
      lookup_key: "",
      can_modify: false,
    },
    role_id_not__in: {
      lookup_key: "",
      can_modify: false,
    },
  };

  const hasInSuffix = (key) => /(__in)$/.test(key);

  const splitBaseAndIn = (key) => {
    if (hasInSuffix(key)) {
      return { baseKey: key.replace(/(__in)$/, ""), hasIn: true };
    }
    return { baseKey: key, hasIn: false };
  };

  const normalizeInKey = (key) => (typeof key === "string" ? key.replace(/(__in)+$/, "__in") : key);

  const deleteBothForms = (params, baseKey) => {
    params.delete(baseKey);
    params.delete(baseKey + LOOKUPS.__IN);
  };

  const isEmpty = (v) =>
    v === null || v === undefined || v === "" || (Array.isArray(v) && v.length === 0);

  // Debounced navigation to prevent excessive requests
  const debouncedNavigate = useCallback(
    (url, replace) => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      debounceTimeoutRef.current = setTimeout(() => {
        navigate(url, { replace });
      }, debounceMs);
    },
    [navigate, debounceMs]
  );

  useEffect(() => {
    const filterString = filters.toString();
    const locationSearch = location.search.startsWith("?")
      ? location.search.slice(1)
      : location.search;

    if (showApplyFiltersButton || filterString === locationSearch) {
      return;
    }

    const url = `${location.pathname}?${filterString}`;

    // Use debounced navigation for filter changes
    if (debounceMs > 0) {
      debouncedNavigate(url, replaceNavigation);
    } else {
      navigate(url, { replace: replaceNavigation });
    }
  }, [
    filters,
    location.pathname,
    navigate,
    showApplyFiltersButton,
    debouncedNavigate,
    debounceMs,
    replaceNavigation,
  ]);

  const getHasUnsyncedChanges = (extraParams = null) => {
    const currentComparable = toComparable(new URLSearchParams(location.search));
    const nextParams = buildNextParams({
      base: filters,
      extraParams,
      showApplyFiltersButton,
    });
    const nextComparable = toComparable(nextParams);
    return currentComparable !== nextComparable;
  };

  const applyToUrl = (opts = { replace: true }, extraParams = null) => {
    const next = buildNextParams({
      base: filters,
      extraParams,
      showApplyFiltersButton,
    });
    const url = `${location.pathname}?${next.toString()}`;
    navigate(url, { replace: !!opts.replace });
  };

  useEffect(() => {
    setFilters(new URLSearchParams(location.search));
  }, [location.search]);

  const hasUnsyncedChanges =
    toComparable(new URLSearchParams(location.search)) !== toComparable(filters);

  const getUpdateFilterCaller = () => {
    const stack = new Error().stack;
    if (!stack) {
      return "unknown";
    }

    const frame = stack
      .split("\n")
      .map((line) => line.trim())
      .find(
        (line) =>
          line &&
          !line.includes("getUpdateFilterCaller") &&
          !line.includes("updateFilter") &&
          !line.startsWith("Error")
      );

    return frame || "unknown";
  };

  const updateFilter = (key, value, bulkNewFilters = null, notApplyOffset = false) => {
    const next = bulkNewFilters || new URLSearchParams(filters);

    if (!showApplyFiltersButton && key !== "offset" && !notApplyOffset) {
      next.set("offset", "0");
    }

    const rawKey = normalizeInKey(String(key));
    const { baseKey, hasIn } = splitBaseAndIn(rawKey);
    const customCfg = customFiltersList[rawKey] || customFiltersList[baseKey];

    const commit = () => {
      if (!bulkNewFilters) {
        setFilters(next);
      }
    };

    if (customCfg && customCfg.can_modify === false) {
      if (isEmpty(value)) {
        next.delete(rawKey);
      } else if (Array.isArray(value)) {
        next.set(rawKey, value.join(","));
      } else {
        next.set(rawKey, value);
      }
      return commit();
    }

    if (isEmpty(value)) {
      deleteBothForms(next, baseKey);
      return commit();
    }

    if (Array.isArray(value)) {
      let targetKey;
      if (hasIn) {
        targetKey = rawKey;
      } else if (customCfg?.can_modify) {
        const lk = normalizeInKey(customCfg.lookup_key || "");
        targetKey = lk ? baseKey + LOOKUPS.__IN : baseKey;
      } else {
        targetKey = value.length > 1 ? baseKey + LOOKUPS.__IN : baseKey;
      }

      if (hasInSuffix(targetKey)) {
        next.delete(baseKey);
      } else {
        next.delete(baseKey + LOOKUPS.__IN);
      }

      next.set(targetKey, value.join(","));
      return commit();
    }

    let targetKey = baseKey;
    if (customCfg?.can_modify && customCfg.lookup_key === LOOKUPS.__IN) {
      targetKey = hasIn ? rawKey : baseKey + LOOKUPS.__IN;
      next.delete(baseKey);
    } else {
      if (["overall_status"].includes(baseKey)) {
        targetKey = baseKey + LOOKUPS.__IN;
        next.delete(baseKey);
      } else {
        next.delete(baseKey + LOOKUPS.__IN);
      }
    }

    next.set(targetKey, value);
    commit();
  };

  const bulkUpdateFilters = (filterMap) => {
    const next = new URLSearchParams(filters);
    for (const [filterKey, filterValue] of filterMap) {
      updateFilter(filterKey, filterValue, next);
    }
    setFilters(next);
  };

  // Wrap setFilters to log before calling the original setFilters
  const setFiltersWithLog = (newFilters) => {
    setFilters(newFilters);
  };

  return {
    filters,
    updateFilter,
    bulkUpdateFilters,
    applyToUrl,
    hasUnsyncedChanges,
    getHasUnsyncedChanges,
    setFilters: setFiltersWithLog,
  };
}
